import {
	PRODUCT_TYPE_BUNDLE,
	PRODUCT_TYPE_CONFIGURABLE,
	PRODUCT_TYPE_GIFTCARD,
	PRODUCT_TYPE_GROUPED,
	PRODUCT_TYPE_SIMPLE,
	PRODUCT_TYPE_VIRTUAL,
} from '@bluheadless/ui-logic/src/constants'
import { useDateFormatter } from '@bluheadless/ui-logic/src/hooks/useDateFormatter'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useProductContext } from '@bluheadless/ui-logic/src/providers/product'
import Typography from '@bluheadless/ui/src/atoms/typography'
import { cx } from '@emotion/css'
import upperFirst from 'lodash/upperFirst'
import { arrayOf, oneOf, shape, string } from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Root } from './estimated-delivery.styled'

const DATE_INPUT_TEMPLATE = 'MM-DD-YYYY'
const DATE_OUTPUT_TEMPLATE = 'ddd DD MMMM'

export const addDaysToDate = (date, days) => {
	const result = new Date(date)
	result.setDate(result.getDate() + days)
	return result
}

const EstimatedDeliveryNotice = ({
	className,
	excludedProductTypes = [PRODUCT_TYPE_GIFTCARD, PRODUCT_TYPE_VIRTUAL],
	dateProps,
	...props
}) => {
	const {
		delivery: {
			estimatedDelivery: { enabled, offsetMin: offsetMinConfig, offsetMax: offsetMaxConfig, excludeDays },
		},
	} = useConfig()

	const productContextValue = useProductContext()

	const canRenderDate = useMemo(
		() =>
			enabled &&
			productContextValue &&
			productContextValue?.[0]?.product?.inventory?.inStock &&
			!excludedProductTypes.includes(productContextValue?.[0]?.product?.type),
		[enabled, excludedProductTypes, productContextValue]
	)

	const [estimatedDays, setEstimatedDays] = useState({
		firstDay: undefined,
		lastDay: undefined,
	})

	const getEstimatedDeliveryDays = useCallback(() => {
		const excludedDays = excludeDays ? excludeDays.split(',')?.map((day) => upperFirst(day)) : []

		let offsetMin = offsetMinConfig
		let offsetMax = offsetMaxConfig

		let firstDay = new Date()
		let lastDay = new Date()

		while (offsetMin > 0) {
			firstDay = addDaysToDate(firstDay, 1)

			if (!excludedDays.includes(firstDay.toLocaleDateString('en-US', { weekday: 'long' }))) {
				offsetMin--
			}
		}

		while (offsetMax > 0) {
			lastDay = addDaysToDate(lastDay, 1)

			if (!excludedDays.includes(lastDay.toLocaleDateString('en-US', { weekday: 'long' }))) {
				offsetMax--
			}
		}

		setEstimatedDays({
			firstDay: firstDay.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
			lastDay: lastDay.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
		})
	}, [excludeDays, offsetMaxConfig, offsetMinConfig])

	const formattedFirstDay = useDateFormatter(estimatedDays?.firstDay, DATE_INPUT_TEMPLATE, DATE_OUTPUT_TEMPLATE)
	const formattedLastDay = useDateFormatter(estimatedDays?.lastDay, DATE_INPUT_TEMPLATE, DATE_OUTPUT_TEMPLATE)

	useEffect(() => {
		if (canRenderDate && !estimatedDays?.firstDay && !estimatedDays?.lastDay) {
			getEstimatedDeliveryDays()
		}
	}, [
		enabled,
		excludedProductTypes,
		estimatedDays?.firstDay,
		getEstimatedDeliveryDays,
		estimatedDays?.lastDay,
		productContextValue,
		canRenderDate,
	])

	return canRenderDate && formattedFirstDay && formattedLastDay ? (
		<Root className={cx('EstimatedDelivery-root', className)} {...props}>
			<Typography variant="body1" {...dateProps}>
				<FormattedMessage
					id="estimated_delivery_notice"
					values={{
						firstDay: formattedFirstDay,
						lastDay: formattedLastDay,
						strong: (chunk) => <strong>{chunk}</strong>,
					}}
				/>
			</Typography>
		</Root>
	) : null
}

EstimatedDeliveryNotice.propTypes = {
	/**
	 * Additional className for the root element.
	 */
	className: string,
	/**
	 * Array of product types to exclude from the estimated delivery notice.
	 * Defaults to `[PRODUCT_TYPE_GIFTCARD, PRODUCT_TYPE_VIRTUAL]`.
	 */
	excludedProductTypes: arrayOf(
		oneOf([
			PRODUCT_TYPE_BUNDLE,
			PRODUCT_TYPE_CONFIGURABLE,
			PRODUCT_TYPE_GIFTCARD,
			PRODUCT_TYPE_GROUPED,
			PRODUCT_TYPE_SIMPLE,
			PRODUCT_TYPE_VIRTUAL,
		])
	),
	/**
	 * Props for the date Typography component.
	 */
	dateProps: shape(Typography.propTypes),
}

export default EstimatedDeliveryNotice
